import { fadeInOut } from '@aiwyn/shared';
import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { catchError, combineLatest, map, of, startWith } from 'rxjs';
import { LoadableStatus } from 'src/app/shared/utils/loadable';
import { EngagementsState } from '../../state/engagements.state';

/** Injected data for {@link EngagementCompleteDialogComponent}. */
export interface EngagementCompleteDialogData {
  /**
   * PP-3635: render the prepayment-failure error state instead of the success "thank you" message. Set
   * when a synchronous prepayment failed during submission (the engagement stays in SENT, not executed).
   */
  prepaymentFailed?: boolean;
}

enum EngagementCompleteDialogHeadline {
  SUBMISSION_RECEIVED = 'Submission received!',
  THANK_YOU = 'Thank you!',
  PAYMENT_FAILED = 'Payment failed',
}

enum EngagementCompleteDialogBody {
  SUBMISSION_RECEIVED = 'You’ll receive an email once processing is complete or if any issues occur. You can access your documents online at any time.',
  THANK_YOU = 'A copy of the signed engagement letter will be emailed to you once everyone else has completed their steps.',
  PAYMENT_FAILED = 'We couldn’t process your prepayment, so your engagement hasn’t been completed. Please review your payment method and try again.',
}

@Component({
  selector: 'app-engagement-complete-dialog',
  templateUrl: './engagement-complete-dialog.component.html',
  styleUrls: ['./engagement-complete-dialog.component.scss'],
  animations: [fadeInOut],
  standalone: false,
})
export class EngagementCompleteDialogComponent {
  readonly LoadableStatus = LoadableStatus;

  /** PP-3635: when true the dialog shows the prepayment-failure error state (no success illustration). */
  readonly prepaymentFailed = this._data?.prepaymentFailed ?? false;

  /** The CTA only closes the dialog; in the failure state it invites the client to retry on the step. */
  readonly continueButtonText = this.prepaymentFailed ? 'Try Again' : 'Continue';

  readonly state$ = combineLatest({
    isSignatory: this._store.select(EngagementsState.isUserSignatoryOnSelectedEngagement),
    allStepsComplete: this._store.select(EngagementsState.allSelectedEngagementStepsComplete),
  }).pipe(
    map(({ isSignatory, allStepsComplete }) => {
      if (this.prepaymentFailed) {
        return {
          status: LoadableStatus.LOADED,
          data: { headline: EngagementCompleteDialogHeadline.PAYMENT_FAILED, body: EngagementCompleteDialogBody.PAYMENT_FAILED },
        };
      }

      let headline: EngagementCompleteDialogHeadline = EngagementCompleteDialogHeadline.THANK_YOU;
      let body: EngagementCompleteDialogBody;

      if (isSignatory) {
        headline = allStepsComplete ? EngagementCompleteDialogHeadline.SUBMISSION_RECEIVED : EngagementCompleteDialogHeadline.THANK_YOU;
        body = allStepsComplete ? EngagementCompleteDialogBody.SUBMISSION_RECEIVED : EngagementCompleteDialogBody.THANK_YOU;
      }

      return { status: LoadableStatus.LOADED, data: { headline, body } };
    }),
    catchError(error => of({ status: LoadableStatus.ERROR, error })),
    startWith({ status: LoadableStatus.LOADING }),
  );

  constructor(
    public readonly dialogRef: MatDialogRef<EngagementCompleteDialogComponent>,
    private readonly _store: Store,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly _data?: EngagementCompleteDialogData,
  ) { }
}
